/**
 * Subscription Banner — 구독 상태 상단 배너 (admin 전용)
 * trialing(종료 7일 전부터)/read_only(체험 종료·미납)/past_due/locked/canceled 안내.
 * 체험 종료 읽기 전용에는 '연장 신청' 진입점을 함께 노출(잠금 D-day 표시).
 * PendingDocumentBanner 패턴 차용. MainLayout 상단에 마운트.
 */

import { AlertTriangle, CalendarPlus, CreditCard, Info, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import EnterpriseInquiryDialog from "@/components/billing/EnterpriseInquiryDialog";
import { Button } from "@/components/ui/button";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useAuthStore } from "@/stores/authStore";

export default function SubscriptionBanner() {
  const [dismissed, setDismissed] = useState(false);
  const user = useAuthStore((s) => s.user);
  const { data } = useSubscriptionStatus();

  // 결제 안내는 관리자에게만 (일반 직원 노출 금지)
  if (!user || user.role !== "admin" || !data) return null;

  const status = data.status;
  const daysLeft = data.days_left ?? null;
  const fromTrial = data.read_only_reason === "trial_expired";
  const daysUntilLock = data.days_until_lock ?? null;

  // 안내가 필요한 상태만 노출
  if (status === "active" || status === "none") return null;

  let tone = "blue";
  let icon = <Info className="w-4 h-4 shrink-0" />;
  let message: React.ReactNode = null;
  let dismissible = true;
  let showExtension = false;

  if (status === "trialing") {
    // 체험 종료 7일 전부터만 안내 (그 전엔 결제 관련 소음 배제)
    if (daysLeft == null || daysLeft > 7) return null;
    message = (
      <>
        무료 체험 중{daysLeft >= 0 ? ` · ${daysLeft}일 남음` : ""}.
        종료 후 결제수단이 없으면 읽기 전용으로 전환됩니다.
      </>
    );
  } else if (status === "past_due") {
    tone = "amber";
    icon = <AlertTriangle className="w-4 h-4 shrink-0" />;
    dismissible = false;
    message = <>결제에 실패했습니다. 기한 내 결제하지 않으면 이용이 제한됩니다.</>;
  } else if (status === "read_only") {
    tone = "red";
    icon = <AlertTriangle className="w-4 h-4 shrink-0" />;
    dismissible = false;
    if (fromTrial) {
      showExtension = true;
      message = (
        <>
          무료 체험이 종료되어 <strong>읽기 전용</strong> 상태입니다.
          {daysUntilLock != null && ` ${daysUntilLock}일 후 서비스가 잠깁니다.`}
          {" "}연장 신청 또는 결제 후 계속 이용할 수 있습니다.
        </>
      );
    } else {
      message = <>미납으로 <strong>읽기 전용</strong> 상태입니다. 결제 후 즉시 복구됩니다.</>;
    }
  } else if (status === "locked") {
    tone = "red";
    icon = <AlertTriangle className="w-4 h-4 shrink-0" />;
    dismissible = false;
    showExtension = true;
    message = <>서비스가 잠겼습니다. 연장 신청 또는 결제를 진행해주세요.</>;
  } else if (status === "canceled") {
    message = <>구독이 해지되었습니다. 기간 종료까지 이용 가능합니다.</>;
  }

  if (dismissed && dismissible) return null;

  const tones: Record<string, string> = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-700",
  };

  return (
    <div className={`border-b px-4 py-2 ${tones[tone]}`}>
      <div className="flex items-center justify-between max-w-screen-xl mx-auto gap-2">
        <div className="flex items-center gap-2 text-sm flex-wrap">
          {icon}
          <span>{message}</span>
          {showExtension && (
            <EnterpriseInquiryDialog
              kind="trial_extension"
              trigger={
                <Button size="sm" variant="outline" className="h-7 bg-white/70">
                  <CalendarPlus className="w-3.5 h-3.5 mr-1" /> 연장 신청
                </Button>
              }
            />
          )}
          <Link to="/billing" className="underline font-medium inline-flex items-center gap-1">
            <CreditCard className="w-3.5 h-3.5" /> 구독·결제
          </Link>
        </div>
        {dismissible && (
          <button onClick={() => setDismissed(true)} className="p-1 opacity-60 hover:opacity-100" aria-label="배너 닫기">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
